import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { TransactionConfirmationModalContainer } from "./TransactionConfirmationModal.styles";
import { Button } from "components/Button";
import { OtpInput } from "components/OtpInput";
import { Loader } from "components/Loader";
import { HelpCenterIcon } from "assets/icons";
import { sendStepUpCode, verifyStepUpCode } from "features/Account/service";
import { toast } from "Layout/slice";
import { toast as sonnerToast } from "sonner";
import { parseErrorMessage } from "utils";

// UI-only cooldown between resends. The real limit is the backend mfa_send
// throttle; this just stops rapid double-taps.
const RESEND_SECONDS = 30;

// Length of the SMS code the backend issues, and the number of boxes shown.
const CODE_LENGTH = 6;

// How long a code the backend already texted stays usable. Re-entering the
// verify step inside this window reuses that code instead of spending another
// send against the user's hourly cap. Keep at or below the backend's code TTL:
// erring short only costs an extra send, erring long shows a screen for a code
// that has already expired.
const CODE_TTL_SECONDS = 4 * 60;

const TransactionConfirmationModal = ({
  show,
  onHide,
  onConfirm,
  transactionData,
  isLoading = false,
  type = "deposit", // 'deposit' or 'withdraw'
  // When true, a fresh SMS code must be verified before the withdrawal is
  // sent. onConfirm is then called with the single-use step-up token.
  requireVerification = false,
  // Set by the parent when the server rejected the transfer for want of a
  // code: request one and go straight to entry, rather than leaving the user
  // on a summary screen with no way to satisfy the demand.
  autoStartVerification = false,
}) => {
  const dispatch = useDispatch();
  const { amount, bankDetails } = transactionData || {};

  // "confirm" shows the transaction summary; "verify" collects the OTP.
  const [step, setStep] = useState("confirm");
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const autoStartedRef = useRef(false);
  // When the last code was texted, and for which amount. Deliberately kept
  // across close/reopen: the code the user is holding does not expire because
  // they closed a modal.
  const codeSentAtRef = useRef(0);
  const codeSentForRef = useRef(null);

  const formattedAmount = useMemo(() => {
    const numeric = Number(amount);
    const absoluteValue = Math.abs(numeric);
    return isNaN(absoluteValue)
      ? "0.00"
      : absoluteValue.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  }, [amount]);

  // The token is bound to the amount server-side, so send exactly what the
  // withdrawal itself submits.
  const stepUpAmount = useMemo(() => parseFloat(amount), [amount]);

  const secondsSinceSend = () =>
    Math.floor((Date.now() - codeSentAtRef.current) / 1000);

  // A code already texted for this exact amount and not yet spent. The token is
  // bound to the amount, so a different one needs its own code.
  const hasOutstandingCode = () =>
    codeSentForRef.current === stepUpAmount &&
    secondsSinceSend() < CODE_TTL_SECONDS;

  // Reset back to the summary whenever the modal is closed so a reopen never
  // lands mid-verification with a stale code. The resend cooldown is recomputed
  // rather than zeroed, so closing and reopening is not a way around it.
  useEffect(() => {
    if (show) {
      setSeconds(Math.max(0, RESEND_SECONDS - secondsSinceSend()));
      return;
    }
    setStep("confirm");
    setCode("");
    setCodeError("");
  }, [show]);

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const intervalId = setInterval(() => setSeconds((s) => s - 1), 1000);
    return () => clearInterval(intervalId);
  }, [seconds]);

  const requestCode = async () => {
    await sendStepUpCode({ action: "withdrawal", amount: stepUpAmount });
    codeSentAtRef.current = Date.now();
    codeSentForRef.current = stepUpAmount;
    setSeconds(RESEND_SECONDS);
  };

  const startVerification = async () => {
    // Show the code screen first and only then decide whether to text one.
    // Every send counts against a small hourly cap, so backing out and pressing
    // Withdraw again must not burn one while the user still holds a live code —
    // Resend is there for when they genuinely need another.
    setStep("verify");
    if (hasOutstandingCode()) return;

    setSendingCode(true);
    try {
      await requestCode();
    } catch (error) {
      dispatch(toast({ type: "error", body: parseErrorMessage(error) }));
    } finally {
      setSendingCode(false);
    }
  };

  const handleConfirmClick = async () => {
    if (!requireVerification) {
      onConfirm();
      return;
    }
    await startVerification();
  };

  const handleVerifySubmit = async () => {
    if (code.length !== CODE_LENGTH) {
      setCodeError(`Enter the ${CODE_LENGTH}-digit code`);
      return;
    }
    setVerifying(true);
    setCodeError("");
    try {
      const { step_up_token } = await verifyStepUpCode({
        action: "withdrawal",
        amount: stepUpAmount,
        mfa_code: code,
      });
      // The code is single use, so it is spent whether or not the withdrawal
      // that follows succeeds. Drop back to the summary first: on failure the
      // user presses Withdraw again and gets a fresh code, instead of being
      // stuck re-submitting one the backend has already consumed.
      codeSentForRef.current = null;
      setCode("");
      setStep("confirm");
      // Hand the token to the parent, which retries the withdrawal with it.
      onConfirm(step_up_token);
    } catch (error) {
      const message = parseErrorMessage(error);
      setCodeError(message);
      sonnerToast.error(message);
    } finally {
      setVerifying(false);
    }
  };

  // Recovery path for a server that demands a code the client did not know was
  // needed. Fires once per opening: pressing Back must return to the summary
  // and stay there, not bounce straight into another code request.
  useEffect(() => {
    if (!show) {
      autoStartedRef.current = false;
      return;
    }
    if (!autoStartVerification || autoStartedRef.current || step !== "confirm")
      return;
    autoStartedRef.current = true;
    startVerification();
  }, [show, autoStartVerification, step]);

  const handleResend = async () => {
    if (seconds > 0) return;
    try {
      await requestCode();
      dispatch(toast({ type: "info", body: "The code has been sent!" }));
    } catch (error) {
      dispatch(toast({ type: "error", body: parseErrorMessage(error) }));
    }
  };

  const handleBackToConfirm = () => {
    setStep("confirm");
    setCode("");
    setCodeError("");
  };

  // Dynamic content based on type
  const modalTitle =
    type === "deposit" ? "Deposit funds" : "Withdraw from Surmount";
  const infoText =
    type === "deposit"
      ? `Are you sure to deposit $${formattedAmount} from Surmount to this bank?`
      : `Are you sure you want to withdraw $${formattedAmount} from Surmount to this bank?`;
  const amountLabel = type === "deposit" ? "Deposit amount" : "Withdraw amount";
  const confirmButtonText = type === "deposit" ? "Deposit" : "Withdraw";

  const isVerifyStep = step === "verify";
  const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;

  return (
    <TransactionConfirmationModalContainer
      show={show}
      onHide={onHide}
      type={type}
    >
      <div className="modal-header">
        <h2 className="modal-title">
          {isVerifyStep ? "Verify withdrawal" : modalTitle}
        </h2>
      </div>

      <div className="modal-body-content">
        {isVerifyStep ? (
          <div className="verification-section">
            <p className="verification-prompt">
              For your security, enter the verification code we texted to your
              phone to withdraw ${formattedAmount} USD.
            </p>

            <OtpInput
              label="Verification code"
              length={CODE_LENGTH}
              value={code}
              onChange={(next) => {
                setCode(next);
                if (codeError) setCodeError("");
              }}
              error={codeError}
              disabled={verifying || isLoading}
              autoFocus
            />

            <button
              type="button"
              className="resend-link"
              disabled={seconds > 0}
              onClick={handleResend}
            >
              {seconds > 0
                ? `Resend code in ${formattedSeconds}s`
                : "Resend code"}
            </button>

            <div className="action-buttons">
              <Button
                variant="secondary"
                size="large"
                width="49%"
                onClick={handleBackToConfirm}
                disabled={verifying || isLoading}
              >
                Back
              </Button>
              <Button
                size="large"
                width="49%"
                onClick={handleVerifySubmit}
                disabled={verifying || isLoading || code.length !== CODE_LENGTH}
              >
                {verifying || isLoading ? (
                  <span className="button-content">
                    <span className="button-loader">
                      <Loader />
                    </span>
                    <span>Verifying...</span>
                  </span>
                ) : (
                  "Verify & withdraw"
                )}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Info Banner - show only for withdraw */}
            {type === "withdraw" && (
              <div className="info-banner">
                <div className="info-icon">
                  <HelpCenterIcon />
                </div>
                <p className="info-text">{infoText}</p>
              </div>
            )}

            {/* Bank Details Section */}
            <div className="bank-details-section">
              <h4 className="section-title">Bank details</h4>

              <div className="bank-detail-row">
                <div className="detail-label">Name</div>
                <div className="detail-value">{bankDetails?.name || "--"}</div>
              </div>

              <div className="bank-detail-row">
                <div className="detail-label">Institution name</div>
                <div className="detail-value">
                  {bankDetails?.institutionName || "--"}
                </div>
              </div>

              <div className="bank-detail-row">
                <div className="detail-label">Account type</div>
                <div className="detail-value">
                  {bankDetails?.accountType || "--"}
                </div>
              </div>

              <div className="bank-detail-row">
                <div className="detail-label">Account number</div>
                <div className="detail-value">
                  {bankDetails?.accountNumber || "--"}
                </div>
              </div>
            </div>

            {/* Amount Section */}
            <div className="amount-section">
              <div className="amount-label">{amountLabel}</div>
              <div className="amount-value">${formattedAmount} USD</div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <Button
                variant="secondary"
                size="large"
                width="49%"
                onClick={onHide}
                disabled={isLoading || sendingCode}
              >
                Cancel
              </Button>
              <Button
                size="large"
                width="49%"
                onClick={handleConfirmClick}
                disabled={isLoading || sendingCode}
              >
                {isLoading || sendingCode ? (
                  <span className="button-content">
                    <span className="button-loader">
                      <Loader />
                    </span>
                    <span>
                      {sendingCode ? "Sending code..." : "Processing..."}
                    </span>
                  </span>
                ) : (
                  confirmButtonText
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </TransactionConfirmationModalContainer>
  );
};

TransactionConfirmationModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  transactionData: PropTypes.shape({
    amount: PropTypes.string,
    bankDetails: PropTypes.shape({
      name: PropTypes.string,
      institutionName: PropTypes.string,
      accountType: PropTypes.string,
      accountNumber: PropTypes.string,
    }),
    availableBalance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  isLoading: PropTypes.bool,
  type: PropTypes.oneOf(["deposit", "withdraw"]).isRequired,
  requireVerification: PropTypes.bool,
  autoStartVerification: PropTypes.bool,
};

export { TransactionConfirmationModal };
